import React, { useState, useEffect } from 'react'
import { Logo, profile } from '../../components/images'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { employer_nav } from './Navs'
import { EAuthData } from '../Provider/EAuthProvider'
import { useAppType } from '../../provider/AppTypeProvider'
import { LogoutButton } from '../../components'
import { FaBars, FaTimes, FaChevronDown, FaChevronUp } from 'react-icons/fa'

const ELayout = ({ children }) => {
    const { user } = EAuthData()
    const navigate = useNavigate()
    const location = useLocation()
    const { appType } = useAppType()
    const [isMenuOpen, setIsMenuOpen] = useState(false)
    const [isMobile, setIsMobile] = useState(false)
    const [activeLink, setActiveLink] = useState(location.pathname)

    // Check if we're on mobile
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 1024) // lg breakpoint
            // Auto-close mobile menu on desktop
            if (window.innerWidth >= 1024) {
                setIsMenuOpen(false)
            }
        }

        checkScreenSize()
        window.addEventListener('resize', checkScreenSize)
        return () => window.removeEventListener('resize', checkScreenSize)
    }, [])

    // Update active link when route changes
    useEffect(() => {
        setActiveLink(location.pathname)
    }, [location.pathname])

    // Close mobile menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobile && isMenuOpen && !event.target.closest('.mobile-sidebar') && !event.target.closest('.menu-toggle')) {
                setIsMenuOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [isMobile, isMenuOpen])

    const handleMenuClick = (path) => {
        navigate(path)
        setActiveLink(path)
        // Close mobile menu when item is clicked
        if (isMobile) {
            setIsMenuOpen(false)
        }
    }

    const toggleMobileMenu = () => {
        setIsMenuOpen(!isMenuOpen)
    }

    // Mobile Header Component
    const MobileHeader = () => (
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-md border-b border-gray-200">
            <div className="flex items-center justify-between p-4">
                {/* Logo */}
                <div className="flex items-center space-x-2">
                    <img src={Logo} className="h-8 w-8 rounded-full" alt="Logo" />
                    <span className="text-lg font-bold">
                        <span className="text-orange-500">Oodles</span> Internships
                    </span>
                </div>

                {/* User Info */}
                <div className="flex items-center space-x-3">
                    <div className="flex items-center space-x-2">
                        <img src={profile} className="h-8 w-8 rounded-full" alt="Profile" />
                        <span className="text-sm font-medium text-gray-700 hidden sm:block">
                            {user?.userd?.displayName}
                        </span>
                    </div>
                    
                    {/* Menu Toggle Button */}
                    <button
                        onClick={toggleMobileMenu}
                        className="menu-toggle p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                        aria-label="Toggle menu"
                    >
                        {isMenuOpen ? (
                            <FaTimes className="h-6 w-6" />
                        ) : (
                            <FaBars className="h-6 w-6" />
                        )}
                    </button>
                </div>
            </div>
        </div>
    )

    // Mobile Sidebar Overlay
    const MobileSidebar = () => (
        <div className={`lg:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
            isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}>
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-black bg-opacity-50"
                onClick={() => setIsMenuOpen(false)}
            />
            
            {/* Sidebar Panel */}
            <div className={`mobile-sidebar fixed left-0 top-0 h-full w-80 max-w-sm bg-white shadow-xl transform transition-transform duration-300 ${
                isMenuOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
                {/* Sidebar Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center space-x-3">
                        <img src={profile} className="h-12 w-12 rounded-full border-2 border-orange-200" alt="Profile" />
                        <div>
                            <p className="font-medium text-gray-900">{user?.userd?.displayName}</p>
                            <p className="text-sm text-gray-600">{appType.charAt(0).toUpperCase() + appType.slice(1)}</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setIsMenuOpen(false)}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-md"
                    >
                        <FaTimes className="h-5 w-5" />
                    </button>
                </div>

                {/* Navigation Menu */}
                <div className="flex-1 overflow-y-auto">
                    <MobileNavigationMenu />
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200 bg-gray-50">
                    <div className="space-y-3">
                        <Link
                            to="/employer/settings"
                            className="flex items-center text-gray-700 hover:text-orange-600"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            Settings
                        </Link>
                        <LogoutButton AuthData={EAuthData} style="w-full" />
                    </div>
                </div>
            </div>
        </div>
    )

    // Desktop Sidebar Component
    const DesktopSidebar = () => (
        <div className="hidden lg:flex lg:fixed lg:inset-y-0 lg:left-0 lg:w-80 lg:bg-gray-100">
            <div className="flex flex-col w-full">
                {/* Sidebar Header */}
                <div className="flex items-center justify-center p-6 border-b border-gray-200 bg-white">
                    <div className="flex items-center space-x-3">
                        <img src={Logo} className="h-10 w-10 rounded-full" alt="Logo" />
                        <span className="text-xl font-bold">
                            <span className="text-orange-500">Oodles</span> Internships
                        </span>
                    </div>
                </div>

                {/* User Profile */}
                <div className="flex items-center justify-center p-6 border-b border-gray-200 bg-white">
                    <div className="flex flex-col items-center space-y-2">
                        <img src={profile} className="h-16 w-16 rounded-full border-2 border-orange-200" alt="Profile" />
                        <div className="text-center">
                            <h2 className="text-lg font-medium text-gray-900">{user?.userd?.displayName}</h2>
                            <p className="text-sm text-gray-600">{appType.charAt(0).toUpperCase() + appType.slice(1)}</p>
                        </div>
                    </div>
                </div>

                {/* Navigation Menu */}
                <div className="flex-1 overflow-y-auto bg-white">
                    <DesktopNavigationMenu />
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 bg-white">
                    <div className="space-y-3">
                        <Link
                            to="/employer/settings"
                            className="block text-center text-gray-700 hover:text-orange-600 font-medium"
                        >
                            Settings
                        </Link>
                        <LogoutButton AuthData={EAuthData} style="w-full" />
                    </div>
                </div>
            </div>
        </div>
    )

    // Shared Navigation Menu Component
    const NavigationMenu = ({ isMobile = false }) => {
        const [openDropdown, setOpenDropdown] = useState(null)

        const handleDropdownToggle = (index) => {
            setOpenDropdown(openDropdown === index ? null : index)
        }

        const isParentActive = (parent, subMenu) => {
            return parent === activeLink || (subMenu && subMenu.some((item) => item.path === activeLink))
        }

        const MenuItem = ({ r, index }) => {
            const isDropdown = r.subMenu && r.subMenu.length > 0
            const parentIsActive = isParentActive(r.path, r.subMenu)
            const filteredSubMenu = r.subMenu ? r.subMenu.filter(subitem => subitem.isMenu) : []

            return (
                <div className={isMobile ? "" : ""}>
                    {/* Main menu item */}
                    <div
                        onClick={() => {
                            if (isDropdown) {
                                handleDropdownToggle(index)
                            } else {
                                handleMenuClick(r.path)
                            }
                        }}
                        className={`flex justify-between items-center px-4 py-3 mx-2 rounded-lg cursor-pointer transition-all duration-200 ${
                            parentIsActive 
                                ? "bg-orange-500 text-white shadow-md" 
                                : "text-gray-700 hover:bg-orange-50 hover:text-orange-600"
                        }`}
                    >
                        <div className="flex items-center space-x-3">
                            <span className="text-lg">{r.icon}</span>
                            <span className="font-medium">{r.name}</span>
                        </div>
                        {isDropdown && (
                            <span className="text-sm">
                                {openDropdown === index ? <FaChevronUp /> : <FaChevronDown />}
                            </span>
                        )}
                    </div>

                    {/* Dropdown menu */}
                    {isDropdown && openDropdown === index && (
                        <div className="mt-1 ml-6 space-y-1">
                            {filteredSubMenu.map((subitem, subIndex) => (
                                <Link
                                    key={subIndex}
                                    to={subitem.path}
                                    onClick={() => handleMenuClick(subitem.path)}
                                    className={`block px-4 py-2 mx-2 rounded-md text-sm transition-all duration-200 ${
                                        activeLink === subitem.path
                                            ? "bg-orange-400 text-white"
                                            : "text-gray-600 hover:bg-orange-50 hover:text-orange-600"
                                    }`}
                                >
                                    {subitem.name}
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            )
        }

        return (
            <nav className="py-4">
                <div className="space-y-2">
                    {employer_nav.map((r, i) => {
                        const shouldRender = r.isMenu && (!r.isPrivate || user)
                        if (shouldRender) {
                            return <MenuItem key={i} r={r} index={i} />
                        }
                        return null
                    })}
                </div>
            </nav>
        )
    }

    // Desktop and Mobile specific navigation menus
    const DesktopNavigationMenu = () => <NavigationMenu isMobile={false} />
    const MobileNavigationMenu = () => <NavigationMenu isMobile={true} />

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Mobile Header */}
            <MobileHeader />

            {/* Mobile Sidebar Overlay */}
            <MobileSidebar />

            {/* Desktop Sidebar */}
            <DesktopSidebar />

            {/* Main Content */}
            <div className={`${isMobile ? 'pt-16' : 'lg:pl-80'} min-h-screen`}>
                <main className="w-full">
                    {children}
                </main>
            </div>
        </div>
    )
}

export default ELayout